import React from 'react';
import TitleBlock from '@/modules/topic/upsert/TitleBlock';
import TopicBasicsBlock from '@/modules/topic/upsert/TopicBasicsBlock';
import EditableWhereWhenBlock from '@/modules/topic/upsert/EditableWhereWhenBlock';
import VisibilityLimitsBlock from '@/modules/topic/upsert/VisibilityLimitsBlock';
import CreateFooter from '@/modules/topic/upsert/CreateFooter';
import { pageHeaderTitleStyle } from '@/modules/topic/read/styles';
import {
  centeredHeaderStyle,
  editBodyStyle,
  pageHeaderRowStyle,
} from '@/modules/topic/upsert/constants';
import type { TopicEditBodyProps } from '../types';
import SharingSetupLink from '../SharingSetupLink';
import SharingSetup from './SharingSetup';

/**
 * Volunteering create/edit body — composed inline from the shared editable sections so
 * the event form can diverge from the other types over time. Topic type is fixed
 * (no type-selection UI); the Sharing card sits last, above
 * the create button on create, so the sharing form can be edited before the
 * topic first saves.
 */
const EditForm: React.FC<TopicEditBodyProps> = ({ upsert, isCreateMode }) => {
  const {
    topicTitle,
    setTopicTitle,
    topicImageData,
    setTopicImageData,
    handleSelectTopicImage,
    topicBody,
    setTopicBody,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    timezone,
    setTimezone,
    timezones,
    endBeforeStart,
    byInviteOnly,
    setByInviteOnly,
    introLimitCountEnabled,
    setIntroLimitCountEnabled,
    introLimitCount,
    setIntroLimitCount,
    globalIntroMax,
    impressionGoal,
    setImpressionGoal,
    onlyManagersCanShare,
    setOnlyManagersCanShare,
    saving,
    canCreateTopics,
    createTopic,
    sharingSetup,
    location,
    setLocation,
  } = upsert;

  return (
    <>
      <div style={centeredHeaderStyle}>
        <div style={pageHeaderRowStyle}>
          <h1 style={pageHeaderTitleStyle}>
            {isCreateMode ? 'New Volunteering' : 'Edit Volunteering'}
          </h1>
        </div>
      </div>

      <div style={editBodyStyle}>
        <TopicBasicsBlock
          titleBlock={
            <TitleBlock topicTitle={topicTitle} setTopicTitle={setTopicTitle} />
          }
          imageData={topicImageData}
          onSelectFile={handleSelectTopicImage}
          onClearImage={() => setTopicImageData(null)}
          topicBody={topicBody}
          setTopicBody={setTopicBody}
        />
        <EditableWhereWhenBlock
          location={location}
          setLocation={setLocation}
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          setEndTime={setEndTime}
          timezone={timezone}
          setTimezone={setTimezone}
          timezones={timezones}
          endBeforeStart={endBeforeStart}
        />
        <VisibilityLimitsBlock
          byInviteOnly={byInviteOnly}
          setByInviteOnly={setByInviteOnly}
          introLimitCountEnabled={introLimitCountEnabled}
          setIntroLimitCountEnabled={setIntroLimitCountEnabled}
          introLimitCount={introLimitCount}
          setIntroLimitCount={setIntroLimitCount}
          globalIntroMax={globalIntroMax}
          impressionGoal={impressionGoal}
          setImpressionGoal={setImpressionGoal}
          onlyManagersCanShare={onlyManagersCanShare}
          setOnlyManagersCanShare={setOnlyManagersCanShare}
        />
        <SharingSetupLink SharingSetup={SharingSetup} draft={sharingSetup} />
        {isCreateMode && (
          <CreateFooter
            endBeforeStart={endBeforeStart}
            saving={saving}
            topicTitle={topicTitle}
            canCreateTopics={canCreateTopics}
            onCreate={createTopic}
          />
        )}
      </div>
    </>
  );
};

export default EditForm;
